import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

const DateRangePicker = ({
  onDateChange,
  setSelectedStartDate,
  selectedStartDate,
  setSelectedStartTime,
  selectedStartTime,
  setSelectedEndDate,
  selectedEndDate,
  setSelectedEndTime,
  selectedEndTime,
  datesOptional,
  maxDaysBetweenDates,
  setVisibility,
  onApply,
  disableFutureDates,
}) => {
  const pickerRef = useRef(null);

  const [pickerVisible, setPickerVisible] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(moment().toDate());
  const [pickerKey, setPickerKey] = useState(0); // Added for re-mounting DayPicker

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setPickerVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [greaterSmallerDateError, setGreaterSmallerDateError] = useState(false);
  const [moreThanMaxDaysDateError, setMoreThanMaxDaysDateError] = useState(false);

  useEffect(() => {
    setPickerVisible(false);
  }, [setVisibility]);

  const validateRange = (range) => {
    const { from, to } = range || {};
    if (!from || !moment(from).isValid()) {
      setGreaterSmallerDateError(false);
      setMoreThanMaxDaysDateError(false);
      return true;
    }
    if (from && !to) {
      setGreaterSmallerDateError(false);
      setMoreThanMaxDaysDateError(false);
      return true;
    }

    if (to && moment(from).isAfter(to)) {
      setGreaterSmallerDateError(true);
      return false;
    }
    setGreaterSmallerDateError(false);

    if (
      to &&
      moment(to).diff(from, 'days') >= maxDaysBetweenDates &&
      maxDaysBetweenDates !== 0
    ) {
      setMoreThanMaxDaysDateError(true);
      return false;
    }
    setMoreThanMaxDaysDateError(false);

    return true;
  };

  const handleDayPickerRangeSelect = (range) => {
    const { from, to } = range || {};
    const now = moment();

    let newStartDate = null;
    let newEndDate = null;

    if (from) {
      // Ensure we get valid time values, defaulting to 00:00
      const startHour = selectedStartTime && selectedStartTime !== '' ?
        moment(selectedStartTime, 'HH:mm').get('hour') : 0;
      const startMinute = selectedStartTime && selectedStartTime !== '' ?
        moment(selectedStartTime, 'HH:mm').get('minute') : 0;

      newStartDate = moment(from).set({
        hour: startHour,
        minute: startMinute,
        second: 0,
        millisecond: 0,
      });

      // Clamp to current moment if disableFutureDates is enabled
      if (disableFutureDates && newStartDate.isAfter(now)) {
        newStartDate = moment(now);
      }

      // Set default start time if not already set
      if (!selectedStartTime || selectedStartTime === '') {
        setSelectedStartTime('00:00');
      }
    }

    if (to) {
      // Ensure we get valid time values, defaulting to 23:59
      const endHour = selectedEndTime && selectedEndTime !== '' ?
        moment(selectedEndTime, 'HH:mm').get('hour') : 23;
      const endMinute = selectedEndTime && selectedEndTime !== '' ?
        moment(selectedEndTime, 'HH:mm').get('minute') : 59;

      newEndDate = moment(to).set({
        hour: endHour,
        minute: endMinute,
        second: 0,
        millisecond: 0,
      });

      // Clamp to current moment if disableFutureDates is enabled
      if (disableFutureDates && newEndDate.isAfter(now)) {
        newEndDate = moment(now);
      }

      // Set default end time if not already set
      if (!selectedEndTime || selectedEndTime === '') {
        setSelectedEndTime('23:59');
      }
    }

    // Always update the dates
    setSelectedStartDate(newStartDate);
    setSelectedEndDate(newEndDate);

    // Validate the range for error display
    validateRange({ from: newStartDate, to: newEndDate });

    // Call onDateChange to keep parent in sync
    onDateChange(newStartDate, newEndDate);
  }; const quickDateSelection = (period) => {
    let start;
    let end;
    const now = moment();
    switch (period) {
      case 'lastWeek':
        start = moment().subtract(1, 'weeks').startOf('isoWeek');
        end = moment().subtract(1, 'weeks').endOf('isoWeek');
        break;
      case 'thisWeek':
        start = moment().startOf('isoWeek');
        end = moment().endOf('isoWeek');
        break;
      case 'thisMonth':
        start = moment().startOf('month');
        end = moment().endOf('month');
        break;
      default:
        start = moment().subtract(period, 'months').startOf('month');
        end = moment().subtract(period, 'months').endOf('month');
    }
    if (maxDaysBetweenDates !== 0 && moment(end).diff(start, 'days') >= maxDaysBetweenDates) {
      end = moment(start).add(maxDaysBetweenDates - 1, 'days').endOf('day');
    } else {
      end = end.endOf('day');
    }

    // Clamp to current moment if disableFutureDates is enabled
    if (disableFutureDates) {
      if (start.isAfter(now)) {
        start = moment(now).startOf('day');
      }
      if (end.isAfter(now)) {
        end = moment(now);
      }
    }

    start = start.startOf('day');
    setCurrentMonth(start.toDate());

    if (!validateRange({ from: start, to: end })) {
      return;
    }

    setSelectedStartDate(start);
    setSelectedStartTime('00:00');

    setSelectedEndDate(end);
    setSelectedEndTime('23:59');
    onDateChange(start, end);
  };

  const handlePickerVisibility = () => {
    setPickerVisible(!pickerVisible);
    if (pickerVisible) {
      setGreaterSmallerDateError(false);
      setMoreThanMaxDaysDateError(false);
    }
  };

  const applyChange = () => {
    if (
      !selectedStartDate ||
      !selectedEndDate ||
      !validateRange({ from: selectedStartDate, to: selectedEndDate })
    ) {
      return;
    }
    setPickerVisible(false);

    if (onApply && typeof onApply === 'function') {
      onApply();
    }
  };

  const formatDateRangeLabel = (start, end) => {
    // If no dates are selected
    if (!start && !end) return 'Select date range';

    // If only start date is selected
    if (start && !end) {
      const startMoment = moment.isMoment(start) ? start : moment(start);
      return `${startMoment.format('MMM DD, YYYY')} - Select end date`;
    }

    // If both dates are selected
    if (start && end) {
      const startMoment = moment.isMoment(start) ? start : moment(start);
      const endMoment = moment.isMoment(end) ? end : moment(end);
      return `${startMoment.format('MMM DD, YYYY')} - ${endMoment.format('MMM DD, YYYY')}`;
    }

    // Fallback
    return 'Select date range';
  }; const handleChangeSelectedStartTime = (event) => {
    const newTime = event.target.value;
    setSelectedStartTime(newTime);

    if (selectedStartDate && moment.isMoment(selectedStartDate)) {
      const updatedStartDate = moment(selectedStartDate).set({
        hour: moment(newTime, 'HH:mm').get('hour'),
        minute: moment(newTime, 'HH:mm').get('minute'),
      });
      validateRange({ from: updatedStartDate, to: selectedEndDate }); // Call for side effects
      setSelectedStartDate(updatedStartDate);
      onDateChange(updatedStartDate, selectedEndDate);
    }
  };

  const handleChangeSelectedEndTime = (event) => {
    const newTime = event.target.value;
    setSelectedEndTime(newTime);

    if (selectedEndDate && moment.isMoment(selectedEndDate)) {
      const updatedEndDate = moment(selectedEndDate).set({
        hour: moment(newTime, 'HH:mm').get('hour'),
        minute: moment(newTime, 'HH:mm').get('minute'),
      });
      validateRange({ from: selectedStartDate, to: updatedEndDate }); // Call for side effects
      setSelectedEndDate(updatedEndDate);
      onDateChange(selectedStartDate, updatedEndDate);
    }
  };

  const pickerAnimation = {
    initial: { opacity: 0, scale: 0.95, y: -10 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.95, y: -10 },
    transition: { duration: 0.2, ease: 'easeInOut' },
  };

  return (
    <div id="dateRangePicker" className="relative flex flex-col items-start font-sans" ref={pickerRef}>
      <div className="flex items-center bg-white border border-gray-300 rounded-lg shadow-sm h-10 hover:shadow-md transition-shadow duration-500">
        <button
          onClick={handlePickerVisibility}
          className="pl-3 pr-2 text-gray-500 hover:text-blue-600 focus:outline-none transition-colors duration-200"
          aria-label="Open date picker"
        >
          <CalendarDaysIcon className="w-5 h-5" />
        </button>
        <span
          onClick={handlePickerVisibility}
          tabIndex="0"
          onKeyPress={(e) => e.key === 'Enter' && handlePickerVisibility()}
          role="button"
          className="px-2 py-2 text-sm text-gray-700 cursor-pointer whitespace-nowrap hover:text-blue-700 transition-colors duration-200"
          id="mainlabel"
        >
          {formatDateRangeLabel(selectedStartDate, selectedEndDate)}
        </span>
        <button
          onClick={handlePickerVisibility}
          className="p-2 text-gray-500 hover:text-blue-600 focus:outline-none transition-colors duration-200 rounded-r-lg"
          aria-label={pickerVisible ? 'Close date picker' : 'Open date picker'}
        >
          {pickerVisible ? (
            <ChevronUpIcon className="w-5 h-5" />
          ) : (
            <ChevronDownIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      <AnimatePresence>
        {pickerVisible && (
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={pickerAnimation}
            transition={pickerAnimation.transition}
            className="absolute z-50 p-5 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl w-[450px] top-full transform origin-top-left"
          >
            <div className="flex flex-col space-y-4">
              <div className="flex flex-col space-y-4">
                <DayPicker
                  key={pickerKey} // Added key prop
                  mode="range"
                  selected={{
                    from: selectedStartDate && moment.isMoment(selectedStartDate) ? selectedStartDate.toDate() : undefined,
                    to: selectedEndDate && moment.isMoment(selectedEndDate) ? selectedEndDate.toDate() : undefined,
                  }}
                  onSelect={handleDayPickerRangeSelect}
                  month={currentMonth}
                  onMonthChange={setCurrentMonth}
                  numberOfMonths={1}
                  disabled={disableFutureDates ? { after: new Date() } : undefined}
                  // make it small
                  className="text-xs"
                  classNames={{
                    day: 'text-gray-700 hover:bg-blue-100 hover:text-blue-600',
                    selected: 'bg-blue-600 text-blac',
                    today: 'bg-blue-50 text-blue-600',
                    disabled: 'text-gray-300',
                  }}
                  modifiersClassNames={{
                    selected: 'bg-blue-600 text-black',
                    today: 'bg-blue-50 text-blue-600',
                    disabled: 'text-gray-300',
                  }}
                  firstDayOfWeek={1}
                  fixedWeeks
                  showOutsideDays
                  showWeekNumber
                  weekStartsOn={1}
                  footer={
                    <div className="flex justify-between items-center mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStartDate(null);
                          setSelectedEndDate(null);
                          onDateChange(null, null);
                          setCurrentMonth(moment().toDate());
                          setPickerKey(prevKey => prevKey + 1); // Force re-mount DayPicker
                        }}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Clear
                      </button>


                      {/* quick Select dropdown */}
                      <select
                        onChange={(e) => quickDateSelection(e.target.value)}
                        className="text-sm text-gray-500 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      >
                        <option value="" disabled selected>
                          Quick Select
                        </option>
                        <option value="lastWeek">Last Week</option>
                        <option value="thisWeek">This Week</option>
                        <option value="thisMonth">This Month</option>
                        {Array.from({ length: 5 }, (_, idx) => {
                          const monthDate = moment().subtract(idx + 1, 'months');
                          return (
                            <option key={monthDate.format('MMMM-YYYY')} value={idx + 1}>
                              {monthDate.format('MMMM YYYY')}
                            </option>
                          );
                        }
                        )}
                      </select>




                    </div>
                  }
                  renderDay={(day, modifiers) => (
                    <div
                      key={day.toString()}
                      className={`${modifiers.selected ? 'bg-blue-600 text-white' : ''
                        } ${modifiers.today ? 'bg-blue-50 text-blue-600' : ''} ${modifiers.disabled ? 'text-gray-300' : ''
                        }`}
                    >
                      {day.getDate()}
                    </div>
                  )}

                />
                <div className="flex justify-between space-x-4 pt-2">
                  <div className="space-y-1 w-1/2">
                    <label htmlFor="inputStartTimeFrom" className="block text-xs font-medium text-slate-600">
                      Start Time
                    </label>
                    <input
                      type="time"
                      id="inputStartTimeFrom"
                      value={selectedStartTime}
                      onChange={handleChangeSelectedStartTime}
                      className="block w-full px-3 py-2 text-sm placeholder-slate-400 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      disabled={!selectedStartDate}
                    />
                  </div>

                  <div className="space-y-1 w-1/2">
                    <label htmlFor="inputEndTimeTo" className="block text-xs font-medium text-slate-600">
                      End Time
                    </label>
                    <input
                      type="time"
                      id="inputEndTimeTo"
                      value={selectedEndTime}
                      onChange={handleChangeSelectedEndTime}
                      className="block w-full px-3 py-2 text-sm placeholder-slate-400 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                      disabled={!selectedEndDate}
                    />
                  </div>
                </div>
              </div>

              {((!selectedStartDate && !datesOptional) || greaterSmallerDateError || moreThanMaxDaysDateError) && (
                <div className="space-y-2">
                  {(!selectedStartDate && !datesOptional) && (
                    <div
                      className="flex items-center p-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md"
                      role="alert"
                    >
                      <ExclamationTriangleIcon className="w-4 h-4 mr-2 text-red-500" />
                      Start date cannot be empty.
                    </div>
                  )}
                  {greaterSmallerDateError && (
                    <div
                      className="flex items-center p-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md"
                      role="alert"
                    >
                      <ExclamationTriangleIcon className="w-4 h-4 mr-2 text-red-500" />
                      From date must be before the To date.
                    </div>
                  )}
                  {moreThanMaxDaysDateError && (
                    <div
                      className="flex items-center p-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md"
                      role="alert"
                    >
                      <ExclamationTriangleIcon className="w-4 h-4 mr-2 text-red-500" />
                      {`Date range cannot exceed ${maxDaysBetweenDates} days.`}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={applyChange}
                className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors duration-200"
                disabled={
                  (!selectedStartDate && !datesOptional) ||
                  !selectedEndDate ||
                  greaterSmallerDateError ||
                  moreThanMaxDaysDateError
                }
              >
                APPLY
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

DateRangePicker.propTypes = {
  onDateChange: PropTypes.func.isRequired,
  setSelectedStartDate: PropTypes.func.isRequired,
  selectedStartDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.object]),
  selectedStartTime: PropTypes.string.isRequired,
  setSelectedStartTime: PropTypes.func.isRequired,
  selectedEndDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.object]),
  setSelectedEndDate: PropTypes.func.isRequired,
  selectedEndTime: PropTypes.string.isRequired,
  setSelectedEndTime: PropTypes.func.isRequired,
  datesOptional: PropTypes.bool,
  maxDaysBetweenDates: PropTypes.number,
  setVisibility: PropTypes.bool,
  onApply: PropTypes.func,
  disableFutureDates: PropTypes.bool,
};

DateRangePicker.defaultProps = {
  datesOptional: true,
  maxDaysBetweenDates: 300000,
  setVisibility: true,
  selectedStartDate: null,
  selectedEndDate: null,
  onApply: undefined,
  disableFutureDates: false,
};

export default DateRangePicker;
